
import { saveAs } from "file-saver";
import { URLAnalysis, KeywordSummary } from "@/types/seo";
import { translateMetric } from "@/utils/seoUtils";

interface CSVGeneratorProps {
  keyword: string;
  urlAnalyses: URLAnalysis[];
  keywordData: KeywordSummary;
}

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '0';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') {
    return value.toLocaleString('pt-BR');
  }
  return value;
};

export const generateCSV = ({ keyword, urlAnalyses, keywordData }: CSVGeneratorProps) => {
  // Get the main analysis (first URL) and competitors
  const mainAnalysis = urlAnalyses[0];
  const competitors = urlAnalyses.slice(1);

  // Define the sections and their metrics based on the frontend structure
  const metrics = [
    {
      section: "Métricas Principais",
      items: [
        { key: "position", label: "Posição no Google" },
        { key: "seo_score", label: "Page Score" },
        { 
          key: "semrush.backlinks.ascore", 
          label: "PageRank",
          getValue: (analysis: URLAnalysis) => {
            const score = analysis.semrush?.backlinks?.ascore ? Math.round(analysis.semrush.backlinks.ascore / 10) : 0;
            return score;
          }
        },
        { 
          key: "backlinks_domains", 
          label: "Domínios de Referência",
          getValue: (analysis: URLAnalysis) => analysis.semrush?.backlinks?.domains_num || analysis.backlinks || 0
        },
        { 
          key: "backlinks_total", 
          label: "Backlinks",
          getValue: (analysis: URLAnalysis) => analysis.semrush?.backlinks?.urls_num || 0
        }
      ]
    },
    {
      section: "Conteúdo",
      items: [
        { key: "title", label: "Título" },
        { key: "title_length", label: "Tamanho do Título" },
        { key: "meta_description", label: "Meta Description" },
        { key: "description_length", label: "Tamanho da Meta Description" },
        { key: "content_length", label: "Tamanho do Conteúdo" }
      ]
    },
    {
      section: "Checklist SEO",
      items: Object.keys(mainAnalysis.boolean_criteria).map(key => ({
        key: `boolean_criteria.${key}`,
        label: translateMetric(key)
      }))
    }
  ];

  // Create CSV rows
  const rows: string[][] = [];

  // Add header row with URL columns
  const headerRow = ['Métrica', mainAnalysis.url];
  competitors.forEach(comp => headerRow.push(comp.url));
  rows.push(headerRow);

  // Add keyword information
  rows.push(['Palavra-chave', keyword, ...Array(competitors.length).fill('')]);
  rows.push(['Volume de Buscas', `${(keywordData.volume || 0).toLocaleString('pt-BR')} pesquisas/mês`, ...Array(competitors.length).fill('')]);

  // Add metrics by section
  metrics.forEach(section => {
    // Add section header
    rows.push([section.section, ...Array(urlAnalyses.length).fill('')]);

    // Add metrics for this section
    section.items.forEach(item => {
      const row = [item.label];
      
      urlAnalyses.forEach(analysis => {
        if (item.getValue) {
          row.push(formatValue(item.getValue(analysis)));
        } else {
          const value = item.key.includes('.')
            ? item.key.split('.').reduce((obj: any, key) => obj?.[key], analysis)
            : analysis[item.key as keyof URLAnalysis];
          row.push(formatValue(value));
        }
      });

      rows.push(row);
    });

    // Add empty row after section
    rows.push(Array(headerRow.length).fill(''));
  });

  // Add footer
  rows.push(['Feito com 💙 por Conversion', ...Array(urlAnalyses.length).fill('')]);

  // Convert rows to CSV string
  const csvContent = rows
    .map(row => 
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma or newline
        const escaped = cell.toString().replace(/"/g, '""');
        return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
      }).join(',')
    )
    .join('\n');

  // Create and download the file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `analise-seo-${keyword}.csv`);
};
